import { makeStyles, Theme } from '@material-ui/core';
import { getStorageItem, STORAGE_ACTION } from '@utils/storage';
import React, { useLayoutEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import SpotlightCTA from './SpotlightCTA/SpotlightCTA';

const $root = document.getElementById('root');

export type Coordinate = { x: number | string; y: number | string };
export type Direction = 'left' | 'top' | 'right' | 'bottom';

export interface SpotlightProps {
  cutOutTarget: Element | Coordinate;
  radiusSize: number;
  CTATitle: string | React.ReactNode;
  CTADirection: Direction;
  CTADescription: string;
  addX?: number;
  addY?: number;
  clientWidth: number;
  clientHeight: number;
  zIndex?: number;
  handleNextStep: () => void;
}

const Spotlight = ({
  cutOutTarget,
  radiusSize,
  CTATitle,
  CTADirection,
  CTADescription,
  addX,
  addY,
  clientWidth,
  clientHeight,
  zIndex = 1210,
  handleNextStep
}: SpotlightProps) => {
  const { svg, circleMask } = useStyles({ zIndex });

  const [close, setClose] = useState(false);
  const [r, setR] = useState(getRadius(clientWidth, clientHeight));
  const [display, setDisplay] = useState(false);

  useLayoutEffect(() => {
    const id = setTimeout(() => {
      setR(radiusSize);
    }, 500);

    return () => {
      clearTimeout(id);
    };
  });

  if (
    !$root ||
    !!getStorageItem(STORAGE_ACTION.NOT_SEEN_AGAIN_CAPA_VIEWER_TUTORIAL) ||
    close ||
    !cutOutTarget
  ) {
    return null;
  }

  let cx, cy;

  if (cutOutTarget instanceof Element) {
    const { cx: nextX, cy: nextY } = getCoordinate(cutOutTarget);
    cx = nextX;
    cy = nextY;
  } else {
    cx = cutOutTarget.x;
    cy = cutOutTarget.y;
  }

  const handleDisplayCTA = () => {
    setDisplay(true);
  };

  return ReactDOM.createPortal(
    <div>
      <svg className={svg} width="100%" height="100%">
        <defs>
          <mask id="circle-mask">
            <rect fill="white" width="100%" height="100%" />
            <circle
              className={circleMask}
              fill="black"
              cy={cy}
              cx={cx}
              r={r}
              onTransitionEnd={handleDisplayCTA}
            />
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          mask="url(#circle-mask)"
          fill="black"
          opacity="0.8"
        />
        <circle
          opacity="0"
          fill="white"
          cy={cy}
          cx={cx}
          r={radiusSize}
          onClick={handleNextStep}
        />
      </svg>
      {display && (
        <SpotlightCTA
          coordinate={{ x: cx, y: cy }}
          CTATitle={CTATitle}
          CTADirection={CTADirection}
          CTADescription={CTADescription}
          addX={addX}
          addY={addY}
          handleClose={() => setClose(true)}
          handleNextStep={handleNextStep}
        />
      )}
    </div>,
    $root
  );
};

export default Spotlight;

const useStyles = makeStyles<Theme, { zIndex: number }>(() => ({
  svg: {
    position: 'absolute',
    zIndex: ({ zIndex }) => zIndex,
    top: 0,
    left: 0
  },
  circleMask: {
    transition: '.2s'
  }
}));

const getRadius = (width: number, height: number) => {
  return Math.sqrt(Math.pow(width, 2) + Math.pow(height, 2));
};

const getCoordinate = (elem: Element) => {
  if (!elem) {
    return { cx: 0, cy: 0 };
  }
  const { x, y, width, height } = elem.getBoundingClientRect();
  const cx = x + width / 2;
  const cy = y + height / 2;

  return { cx, cy };
};
